/**
 * Traffic Layer Management
 *
 * This module handles the traffic flow layer integration with the map
 * using the TomTom Vector Flow Tiles API
 */

import { getMapManager } from '../components/Map/MapManager.js';
import TrafficVectorTileManager from './TrafficVectorTileManager.js';

// Create TrafficVectorTileManager instance with TomTom API key
const trafficManager = new TrafficVectorTileManager('uOBMbLjEEXIElqJZ2NBI2AONtHSkVAto');

// Start with 'relative' flow type which is more reliable
trafficManager.setFlowType('relative');

// Log debug information to help troubleshoot TomTom API connections
console.log('[TrafficLayer] Debug Tile URL:', trafficManager.getDebugTileUrl());

// Track if traffic layer is enabled
let trafficLayerEnabled = false;

// Store the current flow type for toggling
let currentFlowType = 'relative';

/**
 * Add traffic flow layer to the map using vector tiles
 * If already loaded, just makes the layer visible
 */
export function addTrafficLayer() {
    console.log('[TrafficLayer] Adding vector traffic layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[TrafficLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Add the vector tile layer to the map
        trafficManager.addVectorTrafficLayer(map);
        trafficLayerEnabled = true;
        console.log('[TrafficLayer] Added vector traffic layer');
    } catch (error) {
        console.error('[TrafficLayer] Error adding vector traffic layer:', error);
    }
}

/**
 * Remove (hide) traffic flow layer from the map
 * Actually just hides the layer to preserve the data
 */
export function removeTrafficLayer() {
    console.log('[TrafficLayer] Hiding vector traffic layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[TrafficLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Hide the vector tile layer
        trafficManager.removeVectorTrafficLayer(map);
        trafficLayerEnabled = false;
        console.log('[TrafficLayer] Successfully hidden vector traffic layer');
    } catch (error) {
        console.error('[TrafficLayer] Error hiding vector traffic layer:', error);
    }
}

/**
 * Completely remove traffic flow layer and source from the map
 * Used before style changes
 */
export function completelyRemoveTrafficLayer() {
    console.log('[TrafficLayer] Completely removing vector traffic layer');
    const mapManager = getMapManager();
    if (!mapManager || !mapManager.getMap()) {
        console.error('[TrafficLayer] Map not initialized');
        return;
    }

    const map = mapManager.getMap();

    try {
        // Completely remove the vector tile layer and source
        trafficManager.completelyRemoveVectorTrafficLayer(map);
        trafficLayerEnabled = false;
        console.log('[TrafficLayer] Successfully completely removed vector traffic layer');
    } catch (error) {
        console.error('[TrafficLayer] Error completely removing vector traffic layer:', error);
    }
}

// Initialize click handler for traffic button
document.addEventListener('DOMContentLoaded', () => {
    const trafficButton = document.querySelector('.traffic-button');
    if (!trafficButton) {
        console.warn('[TrafficLayer] Traffic button not found');
        return;
    }
    
    // Track time for detecting long press and double-clicks
    let clickTimer = null;
    let lastClickTime = 0;
    
    // Add a tooltip to explain functionality
    trafficButton.title = 'Click to toggle traffic layer. Double-click to switch traffic data type.';
    
    // Track long press for toggling flow type
    trafficButton.addEventListener('mousedown', () => {
        clickTimer = setTimeout(() => {
            clickTimer = null;
            if (trafficLayerEnabled) {
                const mapManager = getMapManager();
                if (mapManager && mapManager.getMap()) {
                    // Toggle flow type and refresh the layer
                    currentFlowType = trafficManager.toggleFlowType(mapManager.getMap());
                    console.log(`[TrafficLayer] Switched to ${currentFlowType} flow type`);
                    // Flash the button to indicate change
                    trafficButton.classList.remove('active');
                    setTimeout(() => {
                        trafficButton.classList.add('active');
                    }, 200);
                }
            }
        }, 1000); // 1 second long press
    });
    
    // Clear timer on mouse up or leave
    const clearTimer = () => {
        if (clickTimer) {
            clearTimeout(clickTimer);
            clickTimer = null;
        }
    };
    
    trafficButton.addEventListener('mouseup', clearTimer);
    trafficButton.addEventListener('mouseleave', clearTimer);
    
    // Regular click handler
    trafficButton.addEventListener('click', (e) => {
        const now = Date.now();
        const isDoubleClick = now - lastClickTime < 300; // 300ms for double-click
        lastClickTime = now;
        
        // If it's a double-click, toggle flow type
        if (isDoubleClick) {
            const mapManager = getMapManager();
            if (mapManager && mapManager.getMap()) {
                currentFlowType = trafficManager.toggleFlowType(mapManager.getMap());
                console.log(`[TrafficLayer] Double-click: Switched to ${currentFlowType} flow type`);
                e.preventDefault(); // Prevent single click action
            }
            return;
        }
        
        // Regular click action
        console.log(`[TrafficLayer] Traffic button clicked, current state: ${trafficLayerEnabled}`);
        
        if (trafficLayerEnabled) {
            removeTrafficLayer();
            trafficButton.classList.remove('active');
        } else {
            addTrafficLayer();
            trafficButton.classList.add('active');
        }
        // trafficLayerEnabled is updated inside the respective functions
    });
});

// Expose functions to window for MapManager
if (typeof window !== 'undefined') {
    window.addTrafficLayer = addTrafficLayer;
    window.removeTrafficLayer = removeTrafficLayer;
    window.completelyRemoveTrafficLayer = completelyRemoveTrafficLayer;
}